import { AlertCircle } from "lucide-react";
import type { ReactNode } from "react";
import { DetailRow } from "@/components/shared/finance/DetailRow";
import { MonoAmount } from "@/components/shared/finance/MonoAmount";
import { MonoId } from "@/components/shared/finance/MonoId";
import { EntityLink } from "@/components/shared/relationships/EntityLink";
import type { PeekEntity } from "@/components/shared/relationships/entity-peek-context";
import type { PeekContentComponent } from "@/components/shared/relationships/peek-registry";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ABSENT_VALUE, formatCurrency, formatEncodedDay, formatDocumentUid } from "@/lib/format";
import { getLineItemProductName } from "@/lib/product-display";

// Shared building blocks for quick-view content. Feature slices compose these
// into their own `<Type>PeekContent` component (usually via `createPeekContent`)
// and register it with `registerPeekType`; nothing here imports a feature slice.

// ---- Shared query/loading/error chrome ----

export interface QueryLike<T> {
  data: T | null | undefined;
  error: Error | null;
  isLoading: boolean;
  refetch: () => unknown;
}

function PeekSkeleton() {
  return (
    <div className="mt-6 space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

/** The capability's inline error state: message plus a retry control. */
export function PeekError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="mt-6 flex flex-col items-start gap-3">
      <div className="flex items-center gap-2 text-destructive-text">
        <AlertCircle className="size-4 text-icon-danger" />
        <span className="text-sm">{message}</span>
      </div>
      <Button onClick={onRetry} size="sm" type="button" variant="outline">
        Retry
      </Button>
    </div>
  );
}

function PeekQueryBody<T>({
  emptyMessage,
  query,
  render,
}: {
  emptyMessage: string;
  query: QueryLike<T>;
  render: (data: T) => ReactNode;
}) {
  if (query.isLoading) {
    return <PeekSkeleton />;
  }
  if (query.error) {
    return <PeekError message="Could not load details." onRetry={() => query.refetch()} />;
  }
  if (!query.data) {
    return <p className="mt-6 text-muted-foreground text-sm">{emptyMessage}</p>;
  }
  return <div className="mt-6 space-y-6">{render(query.data)}</div>;
}

export interface PeekContentConfig<T> {
  emptyMessage: string;
  render: (data: T) => ReactNode;
  useDetail: (id: string) => QueryLike<T>;
}

/** Builds a detail component while keeping its one query hook unconditional. */
export function createPeekContent<T>({
  emptyMessage,
  render,
  useDetail,
}: PeekContentConfig<T>): PeekContentComponent {
  return function PeekContent({ id }: { id: string }) {
    return <PeekQueryBody emptyMessage={emptyMessage} query={useDetail(id)} render={render} />;
  };
}

// ---- Shared content primitives ----
// Every entity's quick-view content is built from these; per-type components
// in the owning slices just map their own data shape onto them, instead of
// each hand-rolling its own header/link/table markup.

export function PeekHeader({ badge, title }: { badge?: ReactNode; title: string }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <MonoId className="text-lg">{title}</MonoId>
      {badge}
    </div>
  );
}

/** A DetailRow whose value is an EntityLink when an id is present, `ABSENT_VALUE` otherwise. */
export function LinkedRow({
  id,
  label,
  name,
  type,
}: {
  id: string | null | undefined;
  label: string;
  name?: string | null;
  type: PeekEntity["type"];
}) {
  return (
    <DetailRow
      label={label}
      value={
        id ? (
          <EntityLink entity={{ id, label: name ?? undefined, type }}>
            {name || ABSENT_VALUE}
          </EntityLink>
        ) : (
          ABSENT_VALUE
        )
      }
    />
  );
}

export interface LineItemRow {
  id: string;
  productId: string | null;
  productLabel: string | null;
  quantity: number | null;
  total: number | null;
}

function LineItemsTable({ emptyMessage, lines }: { emptyMessage: string; lines: LineItemRow[] }) {
  if (lines.length === 0) {
    return <p className="text-muted-foreground text-sm">{emptyMessage}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Product</TableHead>
            <TableHead numeric>Qty</TableHead>
            <TableHead numeric>Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.slice(0, 10).map((line) => (
            <TableRow key={line.id}>
              <TableCell className="text-sm">
                {line.productId ? (
                  <EntityLink
                    entity={{
                      id: line.productId,
                      label: line.productLabel ?? undefined,
                      type: "product",
                    }}
                  >
                    {line.productLabel || ABSENT_VALUE}
                  </EntityLink>
                ) : (
                  line.productLabel || ABSENT_VALUE
                )}
              </TableCell>
              <TableCell numeric>{line.quantity}</TableCell>
              <TableCell numeric>
                <MonoAmount>{formatCurrency(line.total)}</MonoAmount>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function TotalsSummary({
  balance,
  totalAmount,
  totalPaid,
}: {
  balance: number | null;
  totalAmount: number | null;
  totalPaid: number | null;
}) {
  return (
    <div className="space-y-2">
      <DetailRow
        label="Total Amount"
        value={<MonoAmount>{formatCurrency(totalAmount)}</MonoAmount>}
      />
      <DetailRow label="Total Paid" value={<MonoAmount>{formatCurrency(totalPaid)}</MonoAmount>} />
      <DetailRow label="Balance" value={<MonoAmount>{formatCurrency(balance)}</MonoAmount>} />
    </div>
  );
}

export interface RecentDocumentItem {
  balance: number | null;
  id: string;
  uid: string | null;
}

export function RecentDocumentsList({
  emptyMessage,
  items,
  title,
  type,
}: {
  emptyMessage: string;
  items: RecentDocumentItem[];
  title: string;
  type: "invoice" | "purchase-order";
}) {
  return (
    <div>
      <h3 className="mb-3 font-medium text-sm">{title}</h3>
      {items.length > 0 ? (
        <div className="space-y-2">
          {items.slice(0, 5).map((item) => (
            <div className="flex items-center justify-between text-sm" key={item.id}>
              <EntityLink entity={{ id: item.id, label: item.uid ?? undefined, type }}>
                {formatDocumentUid(item.uid, item.id)}
              </EntityLink>
              <MonoAmount>{formatCurrency(item.balance)}</MonoAmount>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">{emptyMessage}</p>
      )}
    </div>
  );
}

// ---- Document summary (invoice / purchase order / estimate) ----
// These three share one shape: header, linked account row, line items, totals.

export interface DocumentSummaryData {
  accountId: string | null;
  accountLabel: string;
  accountName?: string | null;
  badge: ReactNode;
  balance: number | null;
  date: string | null;
  extraRow?: ReactNode;
  lineItemsEmptyMessage: string;
  lines: LineItemRow[];
  linesLabel: string;
  title: string;
  totalAmount: number | null;
  totalPaid: number | null;
}

export function DocumentSummaryContent({ data }: { data: DocumentSummaryData }) {
  return (
    <>
      <PeekHeader badge={data.badge} title={data.title} />
      <div className="space-y-1">
        <LinkedRow
          id={data.accountId}
          label={data.accountLabel}
          name={data.accountName}
          type="account"
        />
        <DetailRow label="Date" value={formatEncodedDay(data.date)} />
        {data.extraRow}
      </div>
      <Separator />
      <div>
        <h3 className="mb-3 font-medium text-sm">
          {data.linesLabel} ({data.lines.length})
        </h3>
        <LineItemsTable emptyMessage={data.lineItemsEmptyMessage} lines={data.lines} />
      </div>
      <Separator />
      <TotalsSummary
        balance={data.balance}
        totalAmount={data.totalAmount}
        totalPaid={data.totalPaid}
      />
    </>
  );
}

export function toLineItemRows(
  lines:
    | Array<{
        id: string;
        line_total: number | null;
        product_id: string | null;
        product_name: string | null;
        quantity: number | null;
        gl_products?: {
          product_name?: string | null;
          vendor_product_name?: string | null;
        } | null;
      }>
    | undefined,
): LineItemRow[] {
  return (lines ?? []).map((line) => ({
    id: line.id,
    productId: line.product_id,
    productLabel: getLineItemProductName(line),
    quantity: line.quantity,
    total: line.line_total,
  }));
}

// ---- Payment summary (customer payment / vendor payment) ----
// Both share one shape: header, amount hero, date/reference, linked account,
// and one linked target document (invoice for customer, PO for vendor).

export interface PaymentSummaryData {
  accountId: string | null;
  accountName?: string | null;
  amount: number | null;
  date: string | null;
  id: string;
  method: string | null;
  reference: string | null;
  targetId?: string | null;
  targetLabel: string;
  targetName?: string | null;
  targetType: "invoice" | "purchase-order";
  uid: string | null;
}

export function PaymentSummaryContent({ payment }: { payment: PaymentSummaryData }) {
  return (
    <>
      <PeekHeader
        badge={payment.method ? <Badge variant="secondary">{payment.method}</Badge> : null}
        title={formatDocumentUid(payment.uid, payment.id)}
      />
      <p className="font-semibold text-2xl">
        <MonoAmount>{formatCurrency(payment.amount)}</MonoAmount>
      </p>
      <Separator />
      <div className="space-y-1">
        <DetailRow label="Date" value={formatEncodedDay(payment.date)} />
        <DetailRow label="Reference" value={payment.reference || ABSENT_VALUE} />
        <LinkedRow
          id={payment.accountId}
          label="Account"
          name={payment.accountName}
          type="account"
        />
        {payment.targetId ? (
          <LinkedRow
            id={payment.targetId}
            label={payment.targetLabel}
            name={payment.targetName}
            type={payment.targetType}
          />
        ) : null}
      </div>
    </>
  );
}
